/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState, useRef, useEffect} from 'react'
import {nanoid} from 'nanoid'
import {useCheckout} from '../../../utils/checkout-context'
import {ToggleCard, ToggleCardEdit, ToggleCardSummary} from '/overrides/app/components/toggle-card'
import {Box} from '@chakra-ui/react'
import ShippingAddressSelection from './shipping-address-selection'
import AddressDisplay from '/overrides/app/components/address-display'
import {
    useShopperCustomersMutation,
    useShopperBasketsMutation,
    useShippingMethodsForShipment
} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@epmc-mach/accelarator/overrides/app/hooks/use-current-customer'
import {useCurrentBasket} from '@epmc-mach/accelarator/overrides/app/hooks/use-current-basket'
import {useCheckoutAddresses} from '../../checkout-addresses-context'
import {useTranslationHelpers} from '@epmc-mach/accelarator/overrides/app/hooks'
import isEqual from 'lodash/isEqual'
import {isDuplicateAddress} from '../../../checkout.helpers'

export default function ShippingAddress() {
    const [isLoading, setIsLoading] = useState()
    const sectionRef = useRef(null)

    // Get all data from Context
    const {
        selectedShippingAddress,
        selectShippingAddress,
        billingSameAsShipping,
        toggleBillingSameAsShipping,
        syncAfterSave,
        refreshAddressData,
        hasInitialized,
        basket,
        customer
    } = useCheckoutAddresses()

    const {refetch: refetchCustomer} = useCurrentCustomer()
    const {refetch: refetchBasket} = useCurrentBasket()

    const shouldShowLoader = isLoading || !hasInitialized

    const {step, STEPS, goToStep, goToNextStep} = useCheckout()
    const createCustomerAddress = useShopperCustomersMutation('createCustomerAddress')
    const updateCustomerAddress = useShopperCustomersMutation('updateCustomerAddress')
    const updateShippingAddressForShipment = useShopperBasketsMutation(
        'updateShippingAddressForShipment'
    )
    const {mutateAsync: updateBillingAddressForBasket} = useShopperBasketsMutation(
        'updateBillingAddressForBasket'
    )
    const updateShipment = useShopperBasketsMutation('updateShipmentForBasket')

    const handleAddressSelect = (address) => {
        selectShippingAddress(address)
    }

    const {tCheckoutPage, tShipping} = useTranslationHelpers()
    const submitButtonMessage = tShipping?.buttonContinue
    const shippingAddressAriaLabel = tShipping?.a11yShippingAddressForm

    const {refetch: refetchShippingMethodsForShipment} = useShippingMethodsForShipment(
        {
            parameters: {
                shipmentId: basket?.shipments?.[0]?.shipmentId,
                basketId: basket?.basketId
            }
        },
        {
            enabled: !!basket?.basketId && !!basket?.shipments?.[0]?.shipmentId
        }
    )

    const submitAndContinue = async (address) => {
        if (isLoading) {
            return
        }

        setIsLoading(true)
        try {
            if (document.activeElement) {
                document.activeElement.blur()
            }

            selectShippingAddress(address)

            const workingAddress = Object.entries({
                address1: address?.address1,
                address2: address?.address2,
                c_address3: address?.c_address3,
                companyName: address?.companyName,
                city: address?.city,
                countryCode: address?.countryCode?.toUpperCase(),
                firstName: address?.firstName,
                lastName: address?.lastName,
                phone: address?.phone,
                postalCode: address?.postalCode,
                stateCode: address?.stateCode
            })
                .filter(([key, value]) => value !== undefined)
                .reduce((obj, [key, value]) => {
                    obj[key] = value
                    return obj
                }, {})

            const {
                id: shippingAddressId,
                fullName: shippingAddressFullName,
                ...restCurrentShippingAddress
            } = basket?.shipments?.[0]?.shippingAddress || {}

            if (!isEqual(workingAddress, restCurrentShippingAddress)) {
                await updateShippingAddressForShipment.mutateAsync({
                    parameters: {
                        basketId: basket.basketId,
                        shipmentId: basket?.shipments?.[0]?.shipmentId,
                        useAsBilling: false
                    },
                    body: workingAddress
                })
            }

            if (customer?.isRegistered) {
                const addressId = address?.addressId
                if (!addressId) {
                    const existingAddresses = customer?.addresses || []
                    const duplicateFound = isDuplicateAddress(workingAddress, existingAddresses)
                    if (!duplicateFound) {
                        await createCustomerAddress.mutateAsync({
                            body: {
                                ...workingAddress,
                                addressId: `${address?.city.replace(/\s+/g, '-')}-${nanoid(5)}`
                            },
                            parameters: {customerId: customer.customerId}
                        })
                    }
                } else {
                    await updateCustomerAddress.mutateAsync({
                        body: {
                            ...address,
                            countryCode: workingAddress?.countryCode
                        },
                        parameters: {
                            customerId: customer.customerId,
                            addressName: addressId
                        }
                    })
                }

                await refetchCustomer()
                await new Promise((resolve) => setTimeout(resolve, 100))
            }

            const shippingMethods = await refetchShippingMethodsForShipment()

            if (
                basket?.shipments?.[0]?.shippingMethod?.id !==
                shippingMethods?.data?.applicableShippingMethods?.[0]?.id
            ) {
                await updateShipment.mutateAsync({
                    parameters: {
                        basketId: basket.basketId,
                        shipmentId: basket?.shipments?.[0]?.shipmentId
                    },
                    body: {
                        shippingMethod: {
                            id: shippingMethods?.data?.applicableShippingMethods?.[0]?.id
                        }
                    }
                })
            }

            const wasCheckboxChecked = billingSameAsShipping

            await syncAfterSave()
            await refreshAddressData()

            if (wasCheckboxChecked) {
                setTimeout(async () => {
                    const freshBasket = await refetchBasket()
                    const savedShippingAddress = freshBasket.data?.shipments?.[0]?.shippingAddress
                    if (savedShippingAddress) {
                        selectShippingAddress(savedShippingAddress)
                    }
                    toggleBillingSameAsShipping(true)
                }, 10)
            }
            if (wasCheckboxChecked) {
                const {
                    id: billingAddressId,
                    fullName: billingAddressFullName,
                    ...restCurrentBillingAddress
                } = basket?.billingAddress || {}
                if (!isEqual(workingAddress, restCurrentBillingAddress)) {
                    await updateBillingAddressForBasket({
                        parameters: {basketId: basket.basketId},
                        body: workingAddress
                    })
                }
            }

            goToNextStep()
            setIsLoading(false)

            setTimeout(() => {
                const nextStepElement = document.querySelector(
                    '[data-testid="sf-toggle-card-step-3"], .checkout-step:not([hidden])'
                )
                const header = document.querySelector('[data-testid="header-block"]')
                const headerHeight = header?.offsetHeight || 0

                if (nextStepElement) {
                    const elementTop = nextStepElement.getBoundingClientRect().top + window.scrollY
                    const offset = elementTop - headerHeight - 16
                    window.scrollTo({
                        top: offset,
                        behavior: 'auto'
                    })
                }
            }, 150)
        } catch (error) {
            console.error('Error in submitAndContinue:', error)
            setIsLoading(false)
        }
    }

    const hasAutoSubmitted = useRef(false)

    const addresses = customer?.addresses

    useEffect(() => {
        if (
            step !== STEPS.SHIPPING_ADDRESS ||
            selectedShippingAddress != null ||
            !addresses?.length
        ) {
            return
        }

        const defaultAddress = addresses?.find((addr) => addr.preferred === true) ?? addresses?.[0]

        if (defaultAddress) {
            selectShippingAddress(defaultAddress)
        }
    }, [selectedShippingAddress, step, addresses, selectShippingAddress])

    useEffect(() => {
        const shouldAutoSubmit =
            step === STEPS.SHIPPING_ADDRESS &&
            selectedShippingAddress &&
            !basket?.shipments?.[0]?.shippingAddress &&
            !hasAutoSubmitted.current

        if (shouldAutoSubmit) {
            hasAutoSubmitted.current = true
            submitAndContinue(selectedShippingAddress)
        }
    }, [selectedShippingAddress, step, basket])

    return (
        <Box ref={sectionRef}>
            <ToggleCard
                id="step-1"
                title={tCheckoutPage?.step2Title}
                editing={step === STEPS.SHIPPING_ADDRESS}
                isLoading={shouldShowLoader}
                disabled={step === STEPS.CONTACT_INFO && !selectedShippingAddress}
                onEdit={() => goToStep(STEPS.SHIPPING_ADDRESS)}
                editLabel={tShipping?.linkEditShipping}
            >
                <ToggleCardEdit>
                    <ShippingAddressSelection
                        selectedAddress={selectedShippingAddress}
                        submitButtonLabel={submitButtonMessage}
                        onSubmit={submitAndContinue}
                        onAddressSelect={handleAddressSelect}
                        formTitleAriaLabel={shippingAddressAriaLabel}
                    />
                </ToggleCardEdit>
                <ToggleCardSummary>
                    <AddressDisplay address={selectedShippingAddress} />
                </ToggleCardSummary>
            </ToggleCard>
        </Box>
    )
}
